import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { FileText, ArrowUp, ChevronRight, Loader } from "lucide-react";
import theme from "../styles/theme";
import { fetchPosts } from "../api";
import PostCard from "../components/feed/PostCard";
import Sidebar from "../components/feed/Sidebar";
import Hero from "../components/layout/Hero";

const T = theme.colors;

export default function FeedPage() {
  const location = useLocation();
  const path = location.pathname.replace("/", "") || "all";
  const typeMap = { all: "all", analysis: "analysis", news: "news", updates: "update" };
  const filterType = typeMap[path] || "all";

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchPosts({ type: filterType });
      setPosts(data.results);
    } catch (err) {
      setError("Failed to load posts. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  return (
    <>
      <Hero />

      <div style={{
        maxWidth: 1200, margin: "0 auto", padding: "32px 24px",
        display: "grid", gridTemplateColumns: "1fr 320px", gap: 28,
      }}>
        {/* Feed column */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Header bar */}
          <div style={{
            display: "flex", alignItems: "center", gap: 12,
            padding: "0 0 12px", borderBottom: `1px solid ${T.border}40`,
          }}>
            <span style={{
              display: "flex", alignItems: "center", gap: 5,
              fontSize: 11, color: T.textDim,
              fontFamily: theme.fonts.mono, letterSpacing: 1,
            }}>
              <FileText size={11} /> {posts.length} {filterType === "all" ? "posts" : filterType}
            </span>
            <div style={{ flex: 1 }} />
            <span style={{
              display: "flex", alignItems: "center", gap: 4,
              fontSize: 11, color: T.textDim, fontFamily: theme.fonts.mono,
            }}>
              <ArrowUp size={10} /> Sorted by: Latest
            </span>
          </div>

          {/* Loading state */}
          {loading && (
            <div style={{ textAlign: "center", padding: 60 }}>
              <Loader
                size={24}
                color={T.violet}
                style={{ animation: "spin 1s linear infinite" }}
              />
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              <p style={{ marginTop: 12, fontSize: 13, color: T.textDim, fontFamily: theme.fonts.mono }}>
                Loading feed...
              </p>
            </div>
          )}

          {/* Error state */}
          {error && !loading && (
            <div style={{
              textAlign: "center", padding: 40,
              background: T.red + "10", border: `1px solid ${T.red}30`,
              borderRadius: 12,
            }}>
              <p style={{ fontSize: 14, color: T.red }}>{error}</p>
              <button
                onClick={loadPosts}
                style={{
                  marginTop: 12, padding: "8px 20px", borderRadius: 8,
                  background: T.purple, color: "#fff", border: "none",
                  fontSize: 13, fontWeight: 600,
                }}
              >
                Retry
              </button>
            </div>
          )}

          {/* Posts */}
          {!loading && !error && (
            <div className="stagger" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              {posts.map((post) => (
                <PostCard key={post.id} post={post} />
              ))}
            </div>
          )}

          {/* Empty state */}
          {!loading && !error && posts.length === 0 && (
            <div style={{ textAlign: "center", padding: 60 }}>
              <p style={{ fontSize: 14, color: T.textDim }}>No posts found for this filter.</p>
            </div>
          )}

          {/* Load more */}
          {!loading && posts.length > 0 && (
            <div style={{ textAlign: "center", padding: 20 }}>
              <button style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "10px 28px", borderRadius: 10,
                border: `1px solid ${T.border}`, background: "transparent",
                color: T.textMuted, fontSize: 13,
                fontFamily: theme.fonts.mono,
              }}>
                <ChevronRight size={14} /> Load more posts
              </button>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <Sidebar />
      </div>
    </>
  );
}
